import { useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import { navigate } from '../App'
import { colors } from '../theme'
import {
  createSequence,
  draftSequence,
  listMailboxes,
  type DraftResponse,
  type MailboxKey,
  type MailboxOption,
  type SequenceStep,
} from '../api/sequences'

type Stage = 'brief' | 'review'

const SAMPLE_BRIEFS = [
  {
    label: 'Boutique hotel surface lots',
    audience:
      "GMs and owners of independent boutique hotels (10-50 rooms) in the US that have an attached or adjacent surface parking lot they're not actively monetizing.",
    goal: 'Book a 15-minute discovery call to show how Parkify QR-pay + Stripe direct deposit turns the lot into a guest amenity AND a new revenue line.',
  },
  {
    label: 'Mid-market property managers',
    audience:
      'Property managers and asset managers running mixed-use portfolios of 3+ buildings in the US Northeast, currently using legacy meter vendors like Flowbird or T2.',
    goal: 'Get a reply confirming whether their meter contract is up for renewal in the next 6 months; if yes, book a portfolio walkthrough.',
  },
  {
    label: 'Marina + event venues',
    audience:
      'Marina general managers and event venue operators in coastal US markets who run seasonal overflow parking with cash or honor boxes.',
    goal: 'Open a conversation by pitching a no-hardware, no-app QR pilot for the upcoming summer season; ask for 10 minutes.',
  },
]

export default function NewSequence() {
  const { authHeaders, apiBase } = useAuth()
  const [stage, setStage] = useState<Stage>('brief')
  const [name, setName] = useState('')
  const [audience, setAudience] = useState('')
  const [goal, setGoal] = useState('')
  const [tone, setTone] = useState('')
  const [stepCount, setStepCount] = useState(4)
  const [daysSpan, setDaysSpan] = useState(14)
  const [busy, setBusy] = useState(false)
  const [draft, setDraft] = useState<DraftResponse | null>(null)
  const [steps, setSteps] = useState<SequenceStep[]>([])
  const [error, setError] = useState<string | null>(null)
  const [mailbox, setMailbox] = useState<MailboxKey>('rep')
  const [mailboxOptions, setMailboxOptions] = useState<MailboxOption[]>([])

  // Load the sender registry once. A failure here is non-fatal: the picker
  // just stays on 'rep', which is the historical behaviour.
  useEffect(() => {
    let cancelled = false
    listMailboxes(apiBase, authHeaders())
      .then((opts) => {
        if (!cancelled) setMailboxOptions(opts)
      })
      .catch(() => {
        /* non-fatal — default sender still works */
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase])

  async function onDraft() {
    setBusy(true)
    setError(null)
    try {
      const result = await draftSequence(apiBase, authHeaders(), {
        name: name || 'New sequence',
        target_audience: audience,
        goal,
        tone,
        step_count: stepCount,
        days_span: daysSpan,
      })
      setDraft(result)
      setSteps(result.steps)
      setStage('review')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Draft failed')
    } finally {
      setBusy(false)
    }
  }

  async function onSave() {
    setBusy(true)
    setError(null)
    try {
      const seq = await createSequence(apiBase, authHeaders(), {
        name: name || 'New sequence',
        description: '',
        target_audience: audience,
        goal,
        tone,
        mailbox,
        steps,
      })
      navigate({ name: 'sequence-detail', id: seq.id })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setBusy(false)
    }
  }

  function applySample(s: (typeof SAMPLE_BRIEFS)[0]) {
    setAudience(s.audience)
    setGoal(s.goal)
    if (!name) setName(s.label)
  }

  return (
    <div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '2.5px',
          color: colors.purple,
          textTransform: 'uppercase',
          marginBottom: 6,
        }}
      >
        New sequence
      </div>
      <h1
        style={{
          fontSize: 32,
          fontWeight: 700,
          letterSpacing: '-1px',
          margin: 0,
          color: colors.black,
        }}
      >
        {stage === 'brief'
          ? 'Describe the audience and the goal'
          : 'Review the drafted steps'}
      </h1>
      <p
        style={{
          color: colors.textMuted,
          maxWidth: 720,
          marginTop: 8,
          marginBottom: 28,
          lineHeight: 1.6,
          fontSize: 14,
        }}
      >
        {stage === 'brief'
          ? 'Write it like you would tell a teammate. The portal expands it into a multi-touch cadence — subject templates, per-contact body prompts, fallback copy.'
          : 'These are the steps the LLM drafted. Edit anything, then save. Body prompts are instructions the LLM uses at send time to write per-contact copy.'}
      </p>

      {error ? <ErrorBlock message={error} /> : null}

      {stage === 'brief' ? (
        <BriefForm
          name={name}
          setName={setName}
          audience={audience}
          setAudience={setAudience}
          goal={goal}
          setGoal={setGoal}
          tone={tone}
          setTone={setTone}
          stepCount={stepCount}
          setStepCount={setStepCount}
          daysSpan={daysSpan}
          setDaysSpan={setDaysSpan}
          samples={SAMPLE_BRIEFS}
          applySample={applySample}
          busy={busy}
          onDraft={onDraft}
        />
      ) : (
        <ReviewForm
          steps={steps}
          setSteps={setSteps}
          rationale={draft?.rationale ?? ''}
          provider={draft?.provider}
          model={draft?.model}
          busy={busy}
          onBack={() => setStage('brief')}
          onSave={onSave}
          mailbox={mailbox}
          setMailbox={setMailbox}
          mailboxOptions={mailboxOptions}
        />
      )}
    </div>
  )
}

function BriefForm(props: {
  name: string
  setName: (v: string) => void
  audience: string
  setAudience: (v: string) => void
  goal: string
  setGoal: (v: string) => void
  tone: string
  setTone: (v: string) => void
  stepCount: number
  setStepCount: (v: number) => void
  daysSpan: number
  setDaysSpan: (v: number) => void
  samples: typeof SAMPLE_BRIEFS
  applySample: (s: (typeof SAMPLE_BRIEFS)[0]) => void
  busy: boolean
  onDraft: () => void
}) {
  const {
    name,
    setName,
    audience,
    setAudience,
    goal,
    setGoal,
    tone,
    setTone,
    stepCount,
    setStepCount,
    daysSpan,
    setDaysSpan,
    samples,
    applySample,
    busy,
    onDraft,
  } = props
  const canDraft = !busy && audience.trim().length >= 12 && goal.trim().length >= 8

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 24 }}>
      <div
        style={{
          background: colors.white,
          border: `1px solid ${colors.divider}`,
          borderRadius: 12,
          padding: 24,
        }}
      >
        <Field
          label="Sequence name"
          hint="Internal label only — reps see this in the list."
        >
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Boutique hotel surface lots"
            style={inputStyle}
          />
        </Field>
        <Field
          label="Target audience"
          hint="Who you're emailing. Titles, regions, company types, vendor cues — be specific."
        >
          <textarea
            value={audience}
            onChange={(e) => setAudience(e.target.value)}
            rows={4}
            placeholder="GMs and owners of independent boutique hotels (10-50 rooms) in the US Northeast that have an attached surface lot they're not actively monetizing."
            style={textareaStyle}
          />
        </Field>
        <Field
          label="Goal"
          hint="What success looks like at the end of the sequence. Pick ONE outcome."
        >
          <textarea
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            rows={3}
            placeholder="Book a 15-minute discovery call to show how Parkify turns the lot into a guest amenity AND a new revenue line."
            style={textareaStyle}
          />
        </Field>
        <Field
          label="Tone (optional)"
          hint="House voice is short, plain, no filler. Add a twist here if you want (e.g. 'more casual', 'more skeptical of vendors')."
        >
          <input
            value={tone}
            onChange={(e) => setTone(e.target.value)}
            placeholder="(leave blank for Parkify house voice)"
            style={inputStyle}
          />
        </Field>
        <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
          <Field label="Steps">
            <select
              value={stepCount}
              onChange={(e) => setStepCount(Number(e.target.value))}
              style={inputStyle}
            >
              {[2, 3, 4, 5, 6, 7, 8].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Days span">
            <select
              value={daysSpan}
              onChange={(e) => setDaysSpan(Number(e.target.value))}
              style={inputStyle}
            >
              {[7, 10, 14, 21, 28, 35, 45, 60].map((n) => (
                <option key={n} value={n}>
                  {n} days
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div
          style={{
            marginTop: 20,
            display: 'flex',
            gap: 10,
            justifyContent: 'flex-end',
          }}
        >
          <button
            onClick={() => navigate({ name: 'sequences' })}
            style={btnGhost}
          >
            Cancel
          </button>
          <button
            disabled={!canDraft}
            onClick={onDraft}
            style={{
              ...btnPrimary,
              opacity: canDraft ? 1 : 0.5,
              cursor: canDraft ? 'pointer' : 'not-allowed',
            }}
          >
            {busy ? 'Drafting…' : 'Draft sequence →'}
          </button>
        </div>
      </div>
      <div>
        <div
          style={{
            background: colors.white,
            border: `1px solid ${colors.divider}`,
            borderRadius: 12,
            padding: 20,
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '2px',
              color: colors.purple,
              textTransform: 'uppercase',
              marginBottom: 12,
            }}
          >
            Sample briefs
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {samples.map((s) => (
              <button
                key={s.label}
                onClick={() => applySample(s)}
                style={{
                  textAlign: 'left',
                  background: colors.bgAlt,
                  border: `1px solid ${colors.divider}`,
                  borderRadius: 10,
                  padding: '12px 14px',
                  cursor: 'pointer',
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: colors.black,
                    marginBottom: 4,
                  }}
                >
                  {s.label}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: colors.textMuted,
                    lineHeight: 1.5,
                  }}
                >
                  {s.audience.slice(0, 110)}…
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function ReviewForm(props: {
  steps: SequenceStep[]
  setSteps: (v: SequenceStep[]) => void
  rationale: string
  provider?: string
  model?: string
  busy: boolean
  onBack: () => void
  onSave: () => void
  mailbox: MailboxKey
  setMailbox: (v: MailboxKey) => void
  mailboxOptions: MailboxOption[]
}) {
  const {
    steps,
    setSteps,
    rationale,
    provider,
    model,
    busy,
    onBack,
    onSave,
    mailbox,
    setMailbox,
    mailboxOptions,
  } = props
  const selected = mailboxOptions.find((m) => m.key === mailbox)
  // Only block on a handle we KNOW is unconfigured. If the registry failed to
  // load, options are empty and we let the save through — the API is the
  // authority and will reject it with a clear message.
  const senderUnavailable = Boolean(selected && !selected.configured)
  function patchStep(i: number, p: Partial<SequenceStep>) {
    setSteps(steps.map((s, idx) => (idx === i ? { ...s, ...p } : s)))
  }
  return (
    <div>
      {rationale ? (
        <div
          style={{
            background: colors.purpleSoft,
            color: colors.black,
            border: `1px solid ${colors.purpleSoftHi}`,
            borderRadius: 10,
            padding: '14px 16px',
            fontSize: 13,
            lineHeight: 1.55,
            marginBottom: 20,
          }}
        >
          <strong style={{ color: colors.purple }}>Why this cadence:</strong>{' '}
          {rationale}
          {provider || model ? (
            <span
              style={{
                marginLeft: 8,
                fontSize: 11,
                color: colors.textMuted,
                letterSpacing: '0.5px',
              }}
            >
              · {provider}
              {model ? ` · ${model}` : ''}
            </span>
          ) : null}
        </div>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {steps.map((s, i) => (
          <div
            key={i}
            style={{
              background: colors.white,
              border: `1px solid ${colors.divider}`,
              borderRadius: 12,
              padding: 20,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginBottom: 14,
              }}
            >
              <div
                style={{
                  background: colors.purple,
                  color: colors.white,
                  borderRadius: 999,
                  padding: '4px 10px',
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase',
                }}
              >
                Step {i + 1}
              </div>
              <div style={{ fontSize: 13, color: colors.textMuted }}>
                Day{' '}
                <input
                  type="number"
                  min={0}
                  max={180}
                  value={s.day_offset}
                  onChange={(e) =>
                    patchStep(i, { day_offset: Number(e.target.value) || 0 })
                  }
                  style={{
                    ...inputStyle,
                    width: 64,
                    display: 'inline-block',
                    padding: '4px 8px',
                    fontSize: 13,
                  }}
                />
              </div>
              <select
                value={s.channel}
                onChange={(e) =>
                  patchStep(i, {
                    channel: e.target.value as SequenceStep['channel'],
                  })
                }
                style={{
                  ...inputStyle,
                  width: 130,
                  padding: '4px 8px',
                  fontSize: 13,
                }}
              >
                <option value="email">email</option>
                <option value="linkedin">linkedin</option>
              </select>
            </div>
            <Field
              label="Subject template"
              hint="Leave empty to ride the prior step's thread (email follow-ups)."
            >
              <input
                value={s.subject_template}
                onChange={(e) =>
                  patchStep(i, { subject_template: e.target.value })
                }
                style={inputStyle}
                placeholder="e.g. quick question about {{company}}'s lot"
              />
            </Field>
            <Field
              label="Body prompt"
              hint="Instructions the LLM uses at send time to write THIS step's body for each contact. Not the final email."
            >
              <textarea
                rows={5}
                value={s.body_prompt}
                onChange={(e) =>
                  patchStep(i, { body_prompt: e.target.value })
                }
                style={textareaStyle}
              />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Field
                label="Fallback subject"
                hint="Used if the per-contact LLM call fails at send time."
              >
                <input
                  value={s.fallback_subject}
                  onChange={(e) =>
                    patchStep(i, { fallback_subject: e.target.value })
                  }
                  style={inputStyle}
                />
              </Field>
              <Field label="Fallback body">
                <textarea
                  rows={3}
                  value={s.fallback_body}
                  onChange={(e) =>
                    patchStep(i, { fallback_body: e.target.value })
                  }
                  style={textareaStyle}
                />
              </Field>
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 24, maxWidth: 560 }}>
        <Field
          label="Send from"
          hint="The mailbox this sequence sends as. Replies land in that inbox."
        >
          <select
            value={mailbox}
            onChange={(e) => setMailbox(e.target.value as MailboxKey)}
            style={inputStyle}
          >
            {mailboxOptions.length === 0 ? (
              <option value="rep">The rep who owns the sequence</option>
            ) : (
              mailboxOptions.map((m) => (
                <option key={m.key} value={m.key} disabled={!m.configured}>
                  {m.label}
                  {m.email ? ` — ${m.email}` : ''}
                  {m.configured ? '' : ' (not set up yet)'}
                </option>
              ))
            )}
          </select>
        </Field>
        {selected?.description ? (
          <div style={{ fontSize: 12, color: colors.textMuted, marginTop: -6 }}>
            {selected.description}
          </div>
        ) : null}
        {senderUnavailable ? (
          <div
            style={{
              marginTop: 10,
              padding: '8px 10px',
              borderRadius: 6,
              fontSize: 12,
              background: colors.purpleSoft,
              border: `1px solid ${colors.purpleSoftHi}`,
              color: colors.black,
            }}
          >
            <strong>{selected?.email || selected?.label}</strong> has no OAuth
            credentials on this deploy yet, so it can’t send. Mint that
            mailbox’s refresh token first, or pick another sender.
          </div>
        ) : null}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 10,
          marginTop: 24,
        }}
      >
        <button onClick={onBack} style={btnGhost}>
          ← Back to brief
        </button>
        <button
          disabled={busy || senderUnavailable}
          onClick={onSave}
          style={{
            ...btnPrimary,
            opacity: busy || senderUnavailable ? 0.6 : 1,
            cursor: busy
              ? 'wait'
              : senderUnavailable
                ? 'not-allowed'
                : 'pointer',
          }}
        >
          {busy ? 'Saving…' : 'Save sequence'}
        </button>
      </div>
    </div>
  )
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label
      style={{
        display: 'block',
        marginBottom: 14,
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '2px',
          color: colors.purple,
          textTransform: 'uppercase',
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      {children}
      {hint ? (
        <div
          style={{
            fontSize: 12,
            color: colors.textMuted,
            marginTop: 4,
            lineHeight: 1.5,
          }}
        >
          {hint}
        </div>
      ) : null}
    </label>
  )
}

function ErrorBlock({ message }: { message: string }) {
  return (
    <div
      style={{
        padding: '14px 18px',
        background: colors.warningBg,
        border: `1.5px solid ${colors.warningBorder}`,
        borderRadius: 10,
        fontSize: 13,
        color: colors.black,
        marginBottom: 20,
      }}
    >
      <strong style={{ color: colors.red }}>Couldn't draft:</strong> {message}
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '10px 12px',
  fontSize: 14,
  fontFamily: 'inherit',
  color: colors.black,
  background: colors.white,
  border: `1px solid ${colors.divider}`,
  borderRadius: 8,
  outline: 'none',
  boxSizing: 'border-box',
}
const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  resize: 'vertical',
  lineHeight: 1.55,
}
const btnPrimary: React.CSSProperties = {
  background: colors.purple,
  color: colors.white,
  border: 'none',
  borderRadius: 10,
  padding: '10px 18px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '1.2px',
  textTransform: 'uppercase',
  cursor: 'pointer',
}
const btnGhost: React.CSSProperties = {
  background: colors.white,
  color: colors.black,
  border: `1px solid ${colors.divider}`,
  borderRadius: 10,
  padding: '10px 18px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '1.2px',
  textTransform: 'uppercase',
  cursor: 'pointer',
}
